import { useState, useRef } from 'react';
import { Edit, X } from 'lucide-react';
import { openFileInEditor, PropsHistory, PreviewManager } from '@/services/editorService';
import { ElementSelector } from './ElementSelector';

interface ElementInfo {
  component: string;
  filePath: string;
  lineNumber?: number;
  selector: string;
  elementType?: string;
  props?: Record<string, any>;
  originalProps?: Record<string, any>;
  parentComponent?: string;
  parentFile?: string;
}

// Mapping of component names to their actual file locations
const COMPONENT_FILE_MAP = {
  Button: 'src/components/ui/button.tsx',
  Link: 'src/components/ui/button.tsx',
  Input: 'src/components/ui/input.tsx',
  Select: 'src/components/ui/select.tsx',
};

const VisualEditor = () => {
  const [isActive, setIsActive] = useState(false);
  const [selectedElement, setSelectedElement] = useState<ElementInfo | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [editedProps, setEditedProps] = useState<Record<string, any> | null>(null);
  const propsHistory = useRef(new PropsHistory());
  const previewManager = useRef(new PreviewManager());

  const getComponentInfo = (element: HTMLElement) => {
    const findReactComponent = (el: HTMLElement) => {
      const keys = Object.keys(el);
      const fiberKey = keys.find(key => 
        key.startsWith('__reactFiber$') || 
        key.startsWith('__reactInternalInstance$')
      );
      
      if (!fiberKey) return null;
      
      let fiber = (el as any)[fiberKey];
      let elementType = null;
      let elementProps = null;
      
      while (fiber) {
        if (fiber.elementType && typeof fiber.elementType !== 'string') {
          elementType = fiber.elementType;
          elementProps = fiber.memoizedProps;
          break;
        }
        if (fiber.type && typeof fiber.type !== 'string') {
          elementType = fiber.type;
          elementProps = fiber.memoizedProps;
          break;
        }
        fiber = fiber.return;
      }
      
      return { fiber, elementType, elementProps };
    };

    const reactComponent = findReactComponent(element);
    
    if (reactComponent?.elementType) {
      const { elementType, elementProps } = reactComponent;
      let name = elementType.displayName || elementType.name || 'Unknown';
      let path = 'src/components/';
      
      const renderSource = elementType.toString();
      
      if (renderSource.includes('Button')) {
        name = 'Button';
        path += 'ui/Button.tsx';
      } else if (renderSource.includes('Link')) {
        name = 'Link';
        path += 'ui/Link.tsx';
      } else if (name.endsWith('Page')) {
        path = `src/pages/${name}.tsx`;
      } else if (name.includes('Layout')) {
        path = `src/components/layout/${name}.tsx`;
      } else if (name.startsWith('Blog')) {
        path = `src/components/blog/${name}.tsx`;
      } else {
        path += `${name}.tsx`;
      }
      
      return { 
        name,
        path,
        elementType: elementType.toString(),
        props: elementProps
      };
    }

    const className = element.className;
    if (className && typeof className === 'string') {
      const matches = className.match(/[A-Z][a-zA-Z]+(?:Button|Card|Section|Container|Widget|Component)/);
      if (matches) {
        const name = matches[0];
        return { name, path: `src/components/${name}.tsx` };
      }
    }

    const parentComponent = element.closest('[data-component]');
    if (parentComponent) {
      const name = parentComponent.getAttribute('data-component') || 'Unknown';
      return { name, path: `src/components/${name}.tsx` };
    }

    return { name: 'Unknown', path: 'Unknown' };
  };

  const getUniqueSelector = (element: HTMLElement): string => {
    if (element.id) return `#${element.id}`;
    
    let selector = element.tagName.toLowerCase();
    
    if (element.className && typeof element.className === 'string') {
      const classes = element.className.split(' ')
        .filter(cls => {
          return /^[a-zA-Z]+(-[a-zA-Z]+)*$/.test(cls) &&
                 !cls.includes(':') &&
                 !cls.includes('[') &&
                 !cls.includes('/');
        });
      
      if (classes.length > 0) {
        selector += `.${classes.join('.')}`;
      }
    }

    Array.from(element.attributes)
      .filter(attr => attr.name.startsWith('data-'))
      .forEach(attr => {
        selector += `[${attr.name}="${attr.value}"]`;
      });
    
    return selector;
  };

  const handleElementSelect = (target: HTMLElement) => {
    if (target.closest('.visual-editor-ui')) {
      return;
    }

    const componentInfo = getComponentInfo(target);
    const selector = getUniqueSelector(target);
    const mappedFilePath = COMPONENT_FILE_MAP[componentInfo?.name || ''] || componentInfo?.path || 'Unknown';

    let parentInfo = null;
    let parentElement = target.parentElement;
    while (parentElement && !parentInfo) {
      const info = getComponentInfo(parentElement);
      if (info?.name && info.name !== componentInfo?.name) {
        parentInfo = info;
        break;
      }
      parentElement = parentElement.parentElement;
    }

    const elementInfo: ElementInfo = {
      component: componentInfo?.name || 'Unknown',
      filePath: mappedFilePath,
      selector: selector,
      elementType: componentInfo?.elementType,
      props: componentInfo?.props,
      originalProps: componentInfo?.props,
      parentComponent: parentInfo?.name,
      parentFile: parentInfo ? COMPONENT_FILE_MAP[parentInfo.name] || parentInfo.path : undefined
    };

    setSelectedElement(elementInfo);
    setIsActive(false);
  };

  return (
    <div className="visual-editor-ui">
      <ElementSelector
        isActive={isActive}
        onSelect={handleElementSelect}
        onCancel={() => setIsActive(false)}
      />
      
      <div className="fixed bottom-4 right-4 z-[100] flex flex-col items-end gap-2">
        {selectedElement && (
          <div className="bg-gradient-to-br from-slate-900/95 to-slate-800/95 backdrop-blur-md text-white rounded-xl p-6 max-w-md relative shadow-xl border border-white/10">
            <button 
              onClick={() => setSelectedElement(null)}
              className="absolute top-3 right-3 text-white/60 hover:text-white transition-all hover:scale-110"
              title="Schließen"
            >
              <X size={18} />
            </button>
            
            <div className="space-y-4">
              {/* Header */}
              <div className="border-b border-white/10 pb-3">
                <h3 className="text-lg font-semibold text-blue-300">
                  Ausgewähltes Element
                </h3>
                <p className="text-sm text-white/60 mt-1">
                  Komponenten-Details und Eigenschaften
                </p>
              </div>
              
              {/* Component Info */}
              <div className="space-y-3">
                <div>
                  <label className="text-xs font-medium text-white/60 block mb-1">
                    Komponente
                  </label>
                  <p className="text-sm font-medium text-white bg-white/5 rounded-md px-3 py-2">
                    {selectedElement.component}
                  </p>
                </div>
                
                <div>
                  <label className="text-xs font-medium text-white/60 block mb-1">
                    Dateipfad
                  </label>
                  <p className="text-sm font-mono text-emerald-300 bg-white/5 rounded-md px-3 py-2 break-all">
                    {selectedElement.filePath}
                  </p>
                </div>
                
                <div>
                  <label className="text-xs font-medium text-white/60 block mb-1">
                    CSS-Selector
                  </label>
                  <p className="text-sm font-mono text-amber-300 bg-white/5 rounded-md px-3 py-2 break-all">
                    {selectedElement.selector}
                  </p>
                </div>
              </div>
              
              {/* Props Section */}
              {selectedElement.props && Object.keys(selectedElement.props).length > 0 && (
                <div className="border-t border-white/10 pt-3">
                  <label className="text-xs font-medium text-white/60 block mb-2">
                    Properties
                  </label>
                  <pre className="text-xs font-mono bg-black/30 p-3 rounded-md max-h-48 overflow-auto scrollbar-thin scrollbar-track-transparent scrollbar-thumb-white/20 hover:scrollbar-thumb-white/30">
                    {JSON.stringify(selectedElement.props, null, 2)}
                  </pre>
                </div>
              )}
              
              {/* Quick Actions */}
              <div className="flex gap-2 pt-2">
                <button 
                  onClick={() => openFileInEditor(selectedElement.filePath)}
                  className="text-xs px-3 py-1.5 bg-blue-500/20 hover:bg-blue-500/30 text-blue-300 rounded-md transition-colors flex items-center gap-1.5"
                >
                  Datei öffnen
                </button>
                {selectedElement.lineNumber && (
                  <button 
                    className="text-xs px-3 py-1.5 bg-white/5 hover:bg-white/10 text-white/80 rounded-md transition-colors"
                    onClick={() => openFileInEditor(selectedElement.filePath, selectedElement.lineNumber)}
                  >
                    Zur Definition springen
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
        <button
          onClick={() => setIsActive(!isActive)}
          className={`bg-black/20 backdrop-blur-sm shadow-lg rounded-full p-3 
            ${isActive ? 'text-blue-400 hover:text-blue-300' : 'text-white/70 hover:text-white'} 
            transition-colors`}
          title={isActive ? "Auswahl abbrechen" : "Element auswählen"}
        >
          {isActive ? <X size={20} /> : <Edit size={20} />}
        </button>
      </div>
    </div>
  );
};

export default VisualEditor;